import {IMutableGridModel} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {
    IVisualStreamId,
    VisualStreamId,
    VisualStreamIdSet
} from '@mintdata-wsp/common/state/ui/visualDesigner/visualData/VisualStreamIds';
import {FrameEntry, TimelineManager} from '@mintdata-wsp/wsp/controllers/visualDesigner/visualData/VisualDataManager';
import {VisualDataUtil} from '@mintdata-wsp/wsp/controllers/visualDesigner/visualData/VisualDataUtil';
import {GridModels} from '@mintdata-wsp/wsp/ui/components/grid/GridModels';
import bind from 'bind-decorator';
import {action, observable, observe} from 'mobx';
import {IArraySplice} from 'mobx/lib/types/observablearray';
import {IDisposer} from 'mobx-utils';

export interface IVisualDataStreamer
{
    isEmpty:boolean;

    reset();

    getGridModel():IMutableGridModel;
}

export class VisualDataStreamer implements IVisualDataStreamer
{
    private readonly _gridModel:IMutableGridModel;
    private readonly _streamId:IVisualStreamId;
    private readonly _timelineManager:TimelineManager;
    private _frameEntriesDisposer:IDisposer;

    @observable
    private _isEmpty:boolean = true;

    constructor(streamId:IVisualStreamId, timelineManager:TimelineManager)
    {
        this._streamId = streamId;
        this._timelineManager = timelineManager;
        this._gridModel = GridModels.newForFrame();

        this.reset();
    }

    @bind
    public getGridModel():IMutableGridModel
    {
        return this._gridModel;
    }

    public get isEmpty():boolean
    {
        return this._isEmpty;
    }

    @bind
    public reset()
    {
        this._isEmpty = true;
        this._gridModel.clearDataRows();

        const frameEntriesSets = this._computeFrameEntriesSets();

        for( let frameEntries of frameEntriesSets )
            this._listenToVisualDataChanges(frameEntries);
    }

    protected _computeFrameEntriesSets():FrameEntry[][]
    {
        const isStreamSet = this._streamId.isStreamSet;
        let frameEntriesSets:FrameEntry[][];

        if( !isStreamSet )
        {
            const singleStreamId = this._streamId as VisualStreamId;
            const frameEntries = this._timelineManager.getOrCreateTimeline(singleStreamId).getFrameEntries();

            frameEntriesSets = [frameEntries];
        }
        else
        {
            frameEntriesSets = [];
            const streamIds = (this._streamId as VisualStreamIdSet).getStreamIds();
            for( let streamId of streamIds )
            {
                const frameEntries = this._timelineManager.getOrCreateTimeline(streamId).getFrameEntries();
                frameEntriesSets.push(frameEntries);
            }
        }

        return frameEntriesSets;
    }

    @bind
    private _listenToVisualDataChanges(frameEntries:FrameEntry[]):void
    {
        this._updateModel(frameEntries);
        this._subscribeToEntries(frameEntries);
    }

    private _subscribeToEntries(frameEntries:FrameEntry[])
    {
        if( this._frameEntriesDisposer )
        {
            this._frameEntriesDisposer();
        }

        this._frameEntriesDisposer = observe(frameEntries, (change:IArraySplice<FrameEntry> | any) =>
        {
            if( change.type === 'splice' )
            {
                const addedFrameEntries = change.added;
                this._updateModel(addedFrameEntries);
            }
        });
    }

    @action.bound
    private _updateModel(addedEntries:FrameEntry[]):void
    {
        this._isEmpty = false;

        for( let addedEntry of addedEntries )
        {
            // console.log(`STREAMER :: saw FrameEntry`, addedEntry.frame);
            // addedEntry.frame.print();

            VisualDataUtil.addRowToGridModel(addedEntry, this._gridModel);
        }
    }
}


